'use client';

import React from 'react';
import Link from 'next/link';
import { CalenddyLogo } from '@/components/brand/CalenddyLogo';
import {
  Calendar,
  Clock,
  MessageCircle,
  CreditCard,
  Users,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Smartphone,
  Tag,
  Scissors
} from 'lucide-react';

export default function FuncionesPage() {
  const features = [
    {
      icon: Clock,
      title: 'Portal de Reservas Online 24/7',
      description:
        'Tus clientes eligen servicio, profesional y horario desde cualquier celular en segundos. Tu enlace calenddy.com/tu-negocio funciona los 365 días del año sin interrupciones.',
      details: ['Multi-servicio en un solo turno (Cross-selling)', 'Totalmente responsive sin apps requeridas', 'Personalización de colores y logo del negocio']
    },
    {
      icon: Calendar,
      title: 'Agenda Multi-Profesional y Horarios',
      description:
        'Cada miembro de tu equipo tiene su propia grilla de horarios. Sincronizá descansos, pausas operativas entre turnos y días bloqueados por vacaciones de forma inmediata.',
      details: ['Turnos simultáneos por profesional', 'Pausas automáticas entre servicios', 'Bloqueos y excepciones de calendario']
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp Oficial y Recordatorios Automáticos',
      description:
        'Vinculá tu WhatsApp mediante código QR en 30 segundos. El sistema envía confirmaciones de reserva, avisos 24 horas antes y enlaces para reprogramar sin que toques tu celular.',
      details: ['Conexión directa vía Baileys Web', 'Envío desde tu propio número telefónico', 'Bandeja de chats y respuestas automáticas']
    },
    {
      icon: CreditCard,
      title: 'Caja, Señas y Mercado Pago',
      description:
        'Exigí seña previa (fija o porcentaje) para asegurar la asistencia de tus turnos. El dinero entra directo a tu cuenta de Mercado Pago con confirmación automática por Webhook.',
      details: ['Arqueo de caja diario (efectivo, MP, tarjetas)', 'Control de señas cobradas y pendientes', 'Precios diferenciados lista vs efectivo']
    },
    {
      icon: Users,
      title: 'Fichas de Clientes y CRM Integrado',
      description:
        'Mantené el registro detallado de cada cliente: historial de visitas pasadas, notas privadas sobre fórmulas o estilos, teléfono de contacto y porcentaje de asistencia.',
      details: ['Historial cronológico de citas', 'Notas técnicas y preferencias del cliente', 'Control de ausencias o cancelaciones']
    },
    {
      icon: Tag,
      title: 'Vouchers, Puntos y Fidelización',
      description:
        'Premiá a tus clientes más leales con cupones de descuento y sistema de acumulación de puntos canjeables por servicios o productos de reventa.',
      details: ['Códigos de descuento con tope de uso', 'Programa de puntos por dinero gastado', 'Gestión de inventario de productos']
    }
  ];

  return (
    <div className="min-h-screen bg-[#F7F7FA] text-[#191923] flex flex-col font-sans selection:bg-[#6255F5] selection:text-white">
      {/* Header */}
      <nav className="w-full bg-white/90 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <CalenddyLogo href="/" size="md" />

          <div className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
            <Link href="/" className="hover:text-[#6255F5] transition-colors">
              Inicio
            </Link>
            <Link href="/funciones" className="text-[#6255F5] font-bold">
              Funciones
            </Link>
            <Link href="/precios" className="hover:text-[#6255F5] transition-colors">
              Precios
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <Link
              href="/ingresar"
              className="text-xs sm:text-sm font-bold text-slate-700 hover:text-[#6255F5] px-3 py-2 rounded-xl transition-colors"
            >
              Ingresar
            </Link>
            <Link
              href="/registro"
              className="px-4 sm:px-5 py-2.5 bg-[#6255F5] hover:bg-[#5144E5] text-white text-xs sm:text-sm font-bold rounded-xl transition-all shadow-md shadow-[#6255F5]/25"
            >
              Empezar gratis
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-16">
          <div className="text-center max-w-2xl mx-auto space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#ECEAFF] text-[#6255F5] text-xs font-bold">
              <Sparkles className="w-3.5 h-3.5" />
              Funcionalidades de Calenddy
            </div>
            <h1 className="text-3xl sm:text-5xl font-black text-[#191923] tracking-tight">
              Potencia operativa para tu negocio
            </h1>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              Descubrí cómo Calenddy te ayuda a automatizar tu agenda, fidelizar clientes y ordenar tus finanzas desde el primer día.
            </p>
          </div>

          {/* Grid de Funcionalidades */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feat, idx) => {
              const Icon = feat.icon;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-3xl p-7 border border-slate-200/90 shadow-sm space-y-4 hover:shadow-md transition"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#ECEAFF] text-[#6255F5] flex items-center justify-center font-bold">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{feat.title}</h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    {feat.description}
                  </p>
                  <div className="pt-2 border-t border-slate-100 space-y-1.5">
                    {feat.details.map((d, dIdx) => (
                      <div key={dIdx} className="flex items-center gap-2 text-xs text-slate-500">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981] shrink-0" />
                        <span>{d}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Banner */}
          <div className="bg-[#6255F5] text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-xl">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight">
              ¿Listo para empezar a usar Calenddy en tu negocio?
            </h2>
            <p className="text-xs sm:text-sm text-white/80 max-w-lg mx-auto">
              Probá todas las funciones de agenda, caja y WhatsApp sin compromiso.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/registro"
                className="px-7 py-3 bg-white text-[#6255F5] font-black text-xs sm:text-sm rounded-xl transition hover:bg-slate-50"
              >
                Crear mi cuenta gratis
              </Link>
              <Link
                href="/precios"
                className="px-7 py-3 bg-white/10 text-white font-bold text-xs sm:text-sm rounded-xl transition hover:bg-white/20 border border-white/20"
              >
                Ver planes y precios
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full bg-[#191923] text-white py-10 px-4 sm:px-6 text-center text-xs text-slate-400">
        <div className="max-w-4xl mx-auto space-y-3">
          <CalenddyLogo variant="light" size="sm" href="/" className="justify-center" />
          <p>© 2026 Calenddy. Plataforma SaaS de Gestión y Reservas.</p>
        </div>
      </footer>
    </div>
  );
}
